import { useState } from 'react';
import { MessageSquare, BookOpen, TrendingUp, Brain, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import LexChatbotPRIsMEModal from './LexChatbotPRIsMEModal';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import { formatLastAnalysisDate } from '../utils/dateFormatter';

interface LexChatbotPRIsMECardProps {
  department: string;
  domain: string;
  index?: number;
  background?: 'default' | 'bz-blue';
}

const lexLeaders = [
  { name: 'Dany Bourassa', title: 'Programmation & alimentation LEX (PRIsME Master)', avatar: '/DB.png' },
  { name: 'Jean-Michel Caouette', title: 'Programmation & alimentation LEX (PRIsME Master)', avatar: '/JMC.png' },
  { name: 'Jean-Pierre Leclerc', title: 'Contrôle qualité LEX', avatar: '/JP.png' },
  { name: 'Pierre-Luc', title: 'Contrôle qualité LEX', avatar: '/PL.png' },
];

export default function LexChatbotPRIsMECard({
  department,
  domain,
  index = 0,
  background = 'default',
}: LexChatbotPRIsMECardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = lexDates['lex_chatbot_prisme_last_ai_analysis'] ?? null;

  return (
    <>
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ scale: 1.02, y: -5 }}
        onClick={() => setIsModalOpen(true)}
        className="relative isolate h-64 w-full overflow-hidden rounded-lg bg-indigo-950 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group border-2 border-indigo-400/40 hover:border-pink-500"
      >
        <div
          className={`absolute inset-0 z-0 transition-opacity duration-300 ${
            background === 'bz-blue'
              ? 'bg-bz-blue opacity-100'
              : 'bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 opacity-90 group-hover:opacity-100'
          }`}
        />
        <div className="absolute inset-0 z-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
          className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-pink-500/40 border border-pink-400/60 rounded-full shadow-lg shadow-pink-500/20"
          title="Je suis LEX — Assistant PRIsME"
        >
          <Brain className="w-4 h-4 text-pink-200" />
          <span className="text-xs text-pink-100 font-semibold">LEX</span>
        </motion.div>

        <div className="relative z-[1] flex h-full flex-col justify-between p-6 text-white">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner group-hover:bg-white/30 transition-colors">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold drop-shadow">LEX — ChatBot PRIsME</h3>
                <p className="text-xs text-white/80 drop-shadow">{department}</p>
              </div>
            </div>
            <p className="text-sm text-white/90 leading-relaxed drop-shadow mb-4">
              PRIsME Master est le cœur : il alimente PRIsME Employé (même base de connaissances). PRIsME Client à venir avec cadre de sécurité.
            </p>
            {lastAnalysisDate && (
              <p className="flex items-center gap-1.5 text-xs text-white/75 drop-shadow">
                <Clock className="h-3.5 w-3.5" />
                Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-white/80">
              <BookOpen className="w-4 h-4" />
              <span>Qui es-tu ? Gouvernance · Fonctionnement</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {lexLeaders.map((leader, idx) => (
                  <div
                    key={idx}
                    className="w-7 h-7 rounded-full border-2 border-white/50 bg-white/20 backdrop-blur-sm overflow-hidden hover:scale-110 transition-transform cursor-pointer"
                    title={`${leader.name} — ${leader.title}`}
                  >
                    <img
                      src={leader.avatar}
                      alt={leader.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        if (target.parentElement) {
                          target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center text-white text-[10px] font-bold">${leader.name.split(' ').map(n => n[0]).join('')}</div>`;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-white">
                <span>En savoir plus</span>
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-0 z-[2] -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
      </motion.button>

      <LexChatbotPRIsMEModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        department={department}
        domain={domain}
      />
    </>
  );
}
